import { computed, Injectable, signal } from '@angular/core';

/**
 * State for the loading bar.
 *
 * Signals rather than `BehaviorSubject`s, and that is the fix rather than a preference.
 * The bar used to be a plain field written from a subscription, so nothing marked the
 * view dirty; `app-root` is `OnPush`, so Angular never walked into the subtree and the
 * bar's `@if` was never re-evaluated. Measured: a ⌘E refresh held a request open for
 * 2.4 s and the bar never entered the DOM, while forcing change detection by hand
 * rendered it instantly. A signal read in a template marks its ancestors for traversal
 * on its own, through `OnPush` and all.
 *
 * The in-flight requests are counted, not keyed by URL. The map used to be
 * `Map<string, boolean>`, so two requests to the same URL — which is every page of the
 * app's paginated `/sync/fetchData` — shared one entry, and the first to finish deleted
 * it and hid the bar while the rest were still running. Measured with two overlapping
 * requests to that URL: the bar was told to hide 172 ms before the second one answered.
 */
@Injectable({ providedIn: 'root' })
export class FuseLoadingService {
  private readonly _auto = signal(true);
  private readonly _mode = signal<'determinate' | 'indeterminate'>(
    'indeterminate'
  );
  private readonly _progress = signal(0);

  /** Set by `show()`/`hide()`, kept apart from what the interceptor counts. */
  private readonly _forced = signal(false);

  /** How many requests are open per URL, so identical URLs cannot collapse. */
  private readonly _pending = signal<ReadonlyMap<string, number>>(new Map());

  readonly auto = this._auto.asReadonly();
  readonly mode = this._mode.asReadonly();
  readonly progress = this._progress.asReadonly();

  /** Requests currently in flight, counted across all URLs. */
  readonly inFlight = computed(() => {
    let total = 0;
    for (const count of this._pending().values()) total += count;
    return total;
  });

  /** What the bar binds to. */
  readonly visible = computed(() => this._forced() || this.inFlight() > 0);

  /**
   * The URLs still open. Nothing reads this — it exists so a bar that will not go away
   * can be asked *what it is waiting for* rather than guessed at.
   */
  readonly pendingUrls = computed(() => [...this._pending().keys()]);

  /** Forces the bar on, regardless of traffic. */
  show(): void {
    this._forced.set(true);
  }

  /**
   * Clears the forced flag. It does not hide a bar that real requests are keeping up:
   * the old version shared one flag between the two, so a `hide()` in the middle of a
   * burst switched the bar off while the app was still loading.
   */
  hide(): void {
    this._forced.set(false);
  }

  setAutoMode(value: boolean): void {
    this._auto.set(value);
  }

  setMode(value: 'determinate' | 'indeterminate'): void {
    this._mode.set(value);
  }

  setProgress(value: number): void {
    if (value < 0 || value > 100) {
      console.error('Loading bar progress must be between 0 and 100');
      return;
    }

    this._progress.set(value);
  }

  /** One more request open on this URL. */
  requestStarted(url: string): void {
    if (!url) return;

    this._pending.update((pending) => {
      const next = new Map(pending);
      next.set(url, (next.get(url) ?? 0) + 1);
      return next;
    });
  }

  /**
   * One fewer request open on this URL. Balanced against `requestStarted` by the
   * interceptor's `finalize`, which runs on success, error and cancellation alike.
   */
  requestFinished(url: string): void {
    if (!url) return;

    this._pending.update((pending) => {
      const open = pending.get(url);
      if (open === undefined) return pending;

      const next = new Map(pending);
      if (open <= 1) {
        next.delete(url);
      } else {
        next.set(url, open - 1);
      }
      return next;
    });
  }
}
